import { usePaneManagement } from '../../contexts/PaneManagementContext';
import { useUpdateWorkflow, useUpdateDefaultWorkflow } from '../../hooks/queries';
import { useAnalytics } from '../../v2/third-party-integration/amplitude';
import BaseCard from './BaseCard';
import { AMPLITUDE_EVENTS } from '../../constants';
import launchExternal from '../../util/launchExternal';
import useAudit from '../../hooks/useAudit';
import { setDocumentTitle } from '../../util/TitleManager';
import { useState } from 'react';
import SetStartupAppDialog from '../Dialogs/SetStartupAppDialog';
import { useUnifyProfileStore, useApplicationStore, useStartupToastStore, useWorkflowsStore } from '../../Store';
import { useTranslation } from 'react-i18next';
import { useFeatureFlag } from '@harnessio/ff-react-client-sdk';

interface Props {
  appId?: string;
  description: string;
  iconColor: string;
  iconText: string;
  id: string;
  isEnabled: boolean;
  isFavorite?: boolean;
  name: string;
  tabIndex?: number;
  url: string;
  cdkuUrl: string;
  isRestricted:boolean;
  restrictedTooltipMsg:string
  openInNewTab?: boolean;
  showNewIndicator?: boolean;
  isDefault?: boolean;
  warningMessage?: string;
  requiredOS?: string[];
}

export default function WorkflowCard({
  appId = '',
  description,
  iconColor,
  iconText,
  id,
  isEnabled,
  isFavorite,
  name,
  tabIndex = 0,
  url,
  cdkuUrl,
  isRestricted,
  restrictedTooltipMsg,
  openInNewTab,
  showNewIndicator,
  isDefault = false,
  warningMessage,
  requiredOS,
}: Props) {
  const { mutate } = useUpdateWorkflow();
  const { mutate: setDefaultWorkflowMutation } = useUpdateDefaultWorkflow();
  const { captureEvent } = useAudit();
  const { openPane, openCdkuPane } = usePaneManagement();
  const [showStartupDialog, setShowStartupDialog] = useState(false);
  const { unifyProfile } = useUnifyProfileStore();
  const applications = useApplicationStore((state: any) => state.applications);
  const workflows = useWorkflowsStore((state: any) => state.workflows);
  const { showStartupToast } = useStartupToastStore();
  const { t } = useTranslation();
  const startupAppFlag = useFeatureFlag('UNIFY_STARTUP_APP');

  const currentDefaultName = (() => {
    if (!unifyProfile?.apps) return '';

    // Check if there's a default app
    const defaultApp = unifyProfile.apps.find(
      (app: any) => app.isDefaultApp === true
    );
    if (defaultApp) {
      const appId = defaultApp.appId;
      return applications?.find((app: any) => app.id === appId)?.name || '';
    }

    // Check if there's a different default workflow
    const defaultWf = unifyProfile.workflows.find(
      (wf: any) =>
        (wf.isDefaultWorkflow === true ) &&
        (wf.workflowId) !== id
    );
    if (defaultWf) {
      const wfId = defaultWf.workflowId;
      return workflows?.find((wf: any) => wf.id === wfId)?.name || '';
    }

    return '';
  })();

  const getDialogScenario = (): 'set' | 'remove' | 'update' => {
    if (isDefault) return 'remove';
    if (currentDefaultName) return 'update';
    return 'set';
  };

  const confirmDefaultWorkflow = () => {
    const settingAsDefault = !isDefault;
    setDefaultWorkflowMutation(
      { id, isDefault: settingAsDefault },
      {
        onSuccess: () => {
          trackEvent(AMPLITUDE_EVENTS.SET_STARTUP, {
            'Workflow': name,
            'Action': settingAsDefault ? 'Set' : 'Remove',
          });
          if (settingAsDefault) {
            showStartupToast(t('baseCard.startupApp.toastSet', { appName: name }), 'positive');
          } else {
            showStartupToast(t('baseCard.startupApp.toastRemove', { appName: name }), 'positive');
          }
        },
        onError: () => {
          showStartupToast(t('baseCard.startupApp.toastError'), 'negative');
        },
      }
    );
    setShowStartupDialog(false);
  };

  const onFavoriteClick = () => {
    mutate({ id, isFavorite: !isFavorite, appId });
    trackFavoriteChange(!isFavorite);
  };
  const { trackEvent } = useAnalytics();
  const trackFavoriteChange = (isFavorite: boolean) => {
    isFavorite &&
      trackEvent(AMPLITUDE_EVENTS.OPEN_WORKFLOW, {
        'Favorite Workflow': name,
      });
  };

  const onOpenClick = () => {
    setDocumentTitle(name, url);
    trackEvent(AMPLITUDE_EVENTS.OPEN_WORKFLOW, {
      Workflow: name,
    });
    launchExternal(url) || openPane(id); 
    captureEvent({action:"Opened",type:"Workflow", name});

  };

  return (
    <>
      <BaseCard
        iconColor={iconColor}
        iconText={iconText}
        onOpenClick={onOpenClick}
        description={description}
        name={name}
        isFavorite={isFavorite}
        isEnabled={isEnabled}
        onFavoriteClick={onFavoriteClick}
        isIconRequired={false}
        tabIndex={tabIndex}
        showDescription={true}
        onCdkuOpen={() => openCdkuPane('CDKU', cdkuUrl)}
        cdkuUrl={cdkuUrl}
        isRestricted={isRestricted}
        restrictedTooltipMsg={restrictedTooltipMsg}
        url={url}
        openInNewTab={openInNewTab}
        id={id}
        showNewIndicator={showNewIndicator as boolean}
        warningMessage={warningMessage}
        requiredOS={requiredOS}
        setDefaultApp={() => setShowStartupDialog(true)}
        isDefault={isDefault}
        showStartupOption={!!url && url.startsWith('http') && Boolean(startupAppFlag)}
      />
      <SetStartupAppDialog
        isOpen={showStartupDialog}
        appName={name}
        scenario={getDialogScenario()}
        previousDefaultAppName={currentDefaultName}
        onConfirm={confirmDefaultWorkflow}
        onDismiss={() => setShowStartupDialog(false)}
      />
    </>
  );
}